/* Small presentational fragments shared by the M6 tabs — React ports of the
   inline-HTML cell helpers in the mockup (verticalChip, dueChip, miniPill, the
   SVG donut + ageing bar). All emit the mockup class names so the scoped CSS
   applies 1:1. */
import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {inr, money} from '../dummyData/format'

export function VerticalChip({vertical}) {
  return <span className={'chip chip-' + String(vertical || '').toLowerCase()}>{vertical}</span>
}
VerticalChip.propTypes = {vertical: PropTypes.string}

export function MiniPill({tone, children, tip}) {
  return (
    <span className={'mini-pill mp-' + tone} title={tip || undefined}>
      {children}
    </span>
  )
}
MiniPill.propTypes = {tone: PropTypes.string, children: PropTypes.node, tip: PropTypes.string}

export function DueChip({owed}) {
  return owed ? <MiniPill tone='amber'>Owed</MiniPill> : <MiniPill tone='green'>Settled</MiniPill>
}
DueChip.propTypes = {owed: PropTypes.bool}

/* Count-up animation hook — mirrors PS.animateCounts for a single value. */
function useCountUp(to, fmt) {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    let raf
    let start = null
    const dur = 700
    const step = (ts) => {
      if (!start) start = ts
      const p = Math.min((ts - start) / dur, 1)
      const e = 1 - Math.pow(1 - p, 3)
      el.textContent = fmt(Math.round(to * e))
      if (p < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [to, fmt])
  return ref
}

export function CountValue({value, money: isMoney, suffix}) {
  const fmt = isMoney
    ? (v) => money(v)
    : (v) => Number(v).toLocaleString('en-IN') + (suffix || '')
  const ref = useCountUp(value, fmt)
  return <span ref={ref}>{isMoney ? '₹0' : '0' + (suffix || '')}</span>
}
CountValue.propTypes = {value: PropTypes.number, money: PropTypes.bool, suffix: PropTypes.string}

/* SVG donut (PS.donut port) — segs:[{label,value,color}], opts:{unit,money}. */
export function Donut({segs, unit = 'total', money: isMoney}) {
  const total = segs.reduce((s, x) => s + (x.value || 0), 0)
  const R = 58
  const C = 2 * Math.PI * R
  const cx = 72
  const cy = 72
  let acc = 0
  const arcs = segs
    .filter((s) => s.value)
    .map((s, i) => {
      const len = (s.value / (total || 1)) * C
      const pct = total ? Math.round((s.value * 100) / total) : 0
      const off = (-acc / (total || 1)) * C
      acc += s.value
      return (
        <circle
          key={i}
          className='donut-seg'
          cx={cx}
          cy={cy}
          r={R}
          fill='none'
          stroke={s.color}
          strokeWidth={20}
          strokeDasharray={`${len.toFixed(2)} ${(C - len).toFixed(2)}`}
          strokeDashoffset={off.toFixed(2)}
        >
          <title>{`${s.label} — ${isMoney ? inr(s.value) : s.value} (${pct}%)`}</title>
        </circle>
      )
    })
  const totalRef = useCountUp(total, isMoney ? (v) => money(v) : (v) => Number(v).toLocaleString('en-IN'))
  return (
    <div className='donut-wrap'>
      <div className='donut-svgwrap'>
        <svg width='144' height='144' viewBox='0 0 144 144' className='donut-svg'>
          <g transform={`rotate(-90 ${cx} ${cy})`}>
            {arcs.length ? (
              arcs
            ) : (
              <circle cx={cx} cy={cy} r={R} fill='none' stroke='#EEF2F6' strokeWidth={20} />
            )}
          </g>
          <text
            ref={totalRef}
            x={cx}
            y={cy - 3}
            textAnchor='middle'
            dominantBaseline='central'
            className='donut-total'
          >
            {isMoney ? '₹0' : '0'}
          </text>
          <text
            x={cx}
            y={cy + 15}
            textAnchor='middle'
            dominantBaseline='central'
            className='donut-unit'
          >
            {unit}
          </text>
        </svg>
      </div>
      <div className='legend'>
        {segs.map((s, i) => {
          const pct = total ? Math.round((s.value * 100) / total) : 0
          return (
            <div className='li' key={i}>
              <span className='sw' style={{background: s.color}} />
              {s.label}
              <span className='v'>{isMoney ? inr(s.value) : s.value}</span>
              <span className='a'>{pct}%</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
Donut.propTypes = {segs: PropTypes.array, unit: PropTypes.string, money: PropTypes.bool}

export const BUCKETS = [
  ['notdue', 'Not yet due', '#1976D2'],
  ['0-3', '0–3d', '#B45309'],
  ['4-7', '4–7d', '#EA580C'],
  ['8-15', '8–15d', '#D84315'],
  ['15+', '15d+', '#C62828'],
]

/* Ageing bar — length encodes volume share, segments the ageing mix (audit #13). */
export function AgeBar({o, maxTotal}) {
  const total = BUCKETS.reduce((s, b) => s + (o[b[0]] || 0), 0) || 1
  const w = maxTotal ? Math.max(8, (total / maxTotal) * 100) : 100
  return (
    <div className='agebar' style={{width: w.toFixed(1) + '%'}}>
      {BUCKETS.map((b) => {
        const n = o[b[0]] || 0
        if (!n) return null
        return (
          <span
            key={b[0]}
            title={`${b[1]}: ${n} AWB(s)`}
            style={{width: ((n / total) * 100).toFixed(1) + '%', background: b[2]}}
          />
        )
      })}
    </div>
  )
}
AgeBar.propTypes = {o: PropTypes.object, maxTotal: PropTypes.number}

export function AgeLegend() {
  return (
    <div className='age-legend'>
      {BUCKETS.map((b) => (
        <div className='li' key={b[0]}>
          <span className='sw' style={{background: b[2]}} />
          {b[1]}
        </div>
      ))}
    </div>
  )
}
